import type { Translations } from "c15t"

type Spelling = `gb` | `us`

const REGION_SPELLING: Record<string, Spelling> = {
  uk: `gb`,
  eu: `gb`,
  us: `us`,
  ca: `us`,
  au: `gb`,
  global: `us`
}

export const spellingForRegion = (region: string | undefined): Spelling =>
  REGION_SPELLING[region || `global`] || `us`

export const consentActionLabels = {
  acceptAll: `Accept All Cookies`,
  rejectAll: `Reject All`,
  customize: `Cookie Settings`,
  save: `Save & Continue`
}

interface BannerDescriptionParts {
  prefix: string
  linkText: string
  suffix: string
}

export const buildBannerDescriptionParts = (
  region: string | undefined
): BannerDescriptionParts => {
  const isGb = spellingForRegion(region) === `gb`
  const personalised = isGb ? `personalised` : `personalized`
  const analyse = isGb ? `analyse` : `analyze`
  return {
    prefix: `By clicking 'Accept All Cookies', you agree to the storing of cookies on your device to enhance site navigation, receive ${personalised} marketing content, and help us ${analyse} site usage. You can find more information in our `,
    linkText: `Cookies Policy`,
    suffix: `.`
  }
}

export const buildTranslations = (
  region: string | undefined
): Partial<Translations> => {
  const isGb = spellingForRegion(region) === `gb`

  const personalisation = isGb ? `personalisation` : `personalization`

  const parts = buildBannerDescriptionParts(region)
  const bannerDescription = `${parts.prefix}${parts.linkText}${parts.suffix}`

  return {
    common: {
      acceptAll: consentActionLabels.acceptAll,
      rejectAll: consentActionLabels.rejectAll,
      customize: consentActionLabels.customize,
      save: consentActionLabels.save,
      close: `Close`,
      securedBy: ``
    },
    cookieBanner: {
      title: `Cookie Consent`,
      description: bannerDescription
    },
    consentManagerDialog: {
      title: `Privacy Preference Center`,
      description: `Our website uses various types of cookies, each of which has a specific function. You can manage your preferences below.`
    },
    consentTypes: {
      necessary: {
        title: `Strictly Necessary Cookies`,
        description: `These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work. These cookies do not store any personally identifiable information.`
      },
      measurement: {
        title: `Performance Cookies`,
        description: `These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site. All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies we will not know when you have visited our site, and will not be able to monitor its performance.`
      },
      functionality: {
        title: `Functional Cookies`,
        description: `These cookies enable the website to provide enhanced functionality and ${personalisation}. They may be set by us or by third party providers whose services we have added to our pages. If you do not allow these cookies then some or all of these services may not function properly.`
      },
      marketing: {
        title: `Targeting Cookies`,
        description: `These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites. They do not store directly personal information, but are based on uniquely identifying your browser and internet device. If you do not allow these cookies, you will experience less targeted advertising.`
      }
    },
    legalLinks: {
      privacyPolicy: `Privacy & Cookies Policy`,
      cookiePolicy: `Privacy & Cookies Policy`,
      termsOfService: `Terms of Service`
    }
  }
}
